import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchMCPayments,
  UploadMCPaymentsCSV,
  FetchMCPaymentInvoice,
  DeleteMCPayment,
  ProcessMCPayments,
  AddSingleMCPayment,
  ValidateSingleMCPayment,
  ProcessMCPayment,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import { MCPayment } from 'src/interfaces/mcPayment'
import { PaymentStatus, SinglePaymentInvalidReason } from 'src/constants/payment/enums'
import { Pagination } from 'src/interfaces/table'

export interface FetchMCPaymentsPayload {
  search?: string
  limit?: number
  offset?: number
  sort?: string
  paymentStatus?: PaymentStatus
}

function* onFetchMCPayments(
  action: AsyncAction<FetchMCPaymentsPayload, { data: Array<MCPayment>; pagination: Pagination }>,
) {
  const { search, limit, offset, sort, paymentStatus } = action.payload

  try {
    const { payload }: { payload: { data: Array<MCPayment>; pagination: Pagination } } = yield call(
      callSecureApi,
      Endpoints.FetchMCPayments,
      {
        apiService: ApiService.Paraworks,
        query: {
          search,
          limit,
          offset,
          sort,
          paymentStatus,
        },
      },
    )

    yield put(FetchMCPayments.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch payments'

    yield put(FetchMCPayments.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadMCPaymentsCSVRequest {
  file: File
}

function* onUploadMCPaymentsCSV(action: AsyncAction<UploadMCPaymentsCSVRequest>) {
  const { file } = action.payload

  try {
    const formData = new FormData()

    formData.append('file', file)

    yield call(callSecureApi, Endpoints.UploadMCPaymentsCSV, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      // returnResponse: true,
    })

    yield put(UploadMCPaymentsCSV.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload payments csv'

    yield put(UploadMCPaymentsCSV.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface FetchMCPaymentInvoicePayload {
  paymentId: string
}

function* onFetchMCPaymentInvoice(
  action: AsyncAction<FetchMCPaymentInvoicePayload, Array<MCPayment>>,
) {
  const { paymentId } = action.payload

  try {
    const { payload }: { payload: Array<MCPayment> } = yield call(
      callSecureApi,
      Endpoints.FetchMCPaymentInvoice,
      {
        apiService: ApiService.Paraworks,
        params: [
          {
            value: paymentId,
            field: ':paymentId',
          },
        ],
        returnBuffer: true,
      },
    )

    yield put(FetchMCPaymentInvoice.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch payments'

    yield put(FetchMCPaymentInvoice.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface DeleteMCPaymentPayload {
  paymentIds: Array<string>
}

function* onDeleteMCPayment(action: AsyncAction<DeleteMCPaymentPayload, Array<MCPayment>>) {
  const { paymentIds } = action.payload

  try {
    const { payload }: { payload: Array<MCPayment> } = yield call(
      callSecureApi,
      Endpoints.DeleteMCPayment,
      {
        method: 'DELETE',
        apiService: ApiService.Paraworks,
        body: {
          earningIds: paymentIds,
        },
      },
    )

    yield put(DeleteMCPayment.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to delete payment'

    yield put(DeleteMCPayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onProcessMCPayments(action: AsyncAction<void, Array<MCPayment>>) {
  try {
    const { payload }: { payload: Array<MCPayment> } = yield call(
      callSecureApi,
      Endpoints.ProcessMCPayments,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
      },
    )

    yield put(ProcessMCPayments.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to process payments'

    yield put(ProcessMCPayments.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface ProcessMCPaymentPayload {
  paymentId: string
}

function* onProcessMCPayment(action: AsyncAction<ProcessMCPaymentPayload, Array<MCPayment>>) {
  const { paymentId } = action.payload

  try {
    const { payload }: { payload: Array<MCPayment> } = yield call(
      callSecureApi,
      Endpoints.ProcessMCPayment,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        params: [
          {
            value: paymentId,
            field: ':paymentId',
          },
        ],
      },
    )

    yield put(ProcessMCPayment.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to process payment'

    yield put(ProcessMCPayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ValidateSingleMCPaymentRequest {
  masterContractorExternalId: string
  amount: number
}

export interface ValidateSingleMCPaymentResponse {
  isValid: boolean
  reason?: SinglePaymentInvalidReason
}

function* onValidateSingleMCPayment(
  action: AsyncAction<ValidateSingleMCPaymentRequest, ValidateSingleMCPaymentResponse>,
) {
  const { masterContractorExternalId, amount } = action.payload

  try {
    const { payload }: { payload: ValidateSingleMCPaymentResponse } = yield call(
      callSecureApi,
      Endpoints.ValidateSingleMCPayment,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: {
          masterContractorExternalId,
          amount,
        },
      },
    )

    yield put(ValidateSingleMCPayment.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to validate payment'

    yield put(ValidateSingleMCPayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface AddSingleMCPaymentRequest {
  masterContractorExternalId: string
  amount: number
}

function* onAddSingleMCPayment(action: AsyncAction<AddSingleMCPaymentRequest>) {
  const { masterContractorExternalId, amount } = action.payload

  try {
    yield call(callSecureApi, Endpoints.AddSingleMCPayment, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        masterContractorExternalId,
        amount,
      },
    })

    yield put(AddSingleMCPayment.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to create payment'

    yield put(AddSingleMCPayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* mcPaymentSaga() {
  yield takeEvery(FetchMCPayments.type.REQUEST, onFetchMCPayments)
  yield takeEvery(UploadMCPaymentsCSV.type.REQUEST, onUploadMCPaymentsCSV)
  yield takeEvery(FetchMCPaymentInvoice.type.REQUEST, onFetchMCPaymentInvoice)
  yield takeEvery(DeleteMCPayment.type.REQUEST, onDeleteMCPayment)
  yield takeEvery(ProcessMCPayments.type.REQUEST, onProcessMCPayments)
  yield takeEvery(ProcessMCPayment.type.REQUEST, onProcessMCPayment)
  yield takeEvery(ValidateSingleMCPayment.type.REQUEST, onValidateSingleMCPayment)
  yield takeEvery(AddSingleMCPayment.type.REQUEST, onAddSingleMCPayment)
}
